import { addFractions, copyFrac, frac, lessThanFraction, subtractFractions } from '@mimir/common/util/integerMath';
import { invariant } from '@mimir/common/util/invariant';
import type { TimelineInternalOps } from './timelineInternalOps';
import type { TimelineModel, ModelVideoTrackBox, ModelAudioTrackBox, ModelTrackBox, ModelTrackBoxLocation } from './timelineModel';
import { getModelTrackBoxDuration, getModelTrackBoxesDuration, hasDuration, isModelVideoTrackBox } from './timelineModel';
import type { MimirItemRef } from './mimirItemRef';
import type { SourceRef } from './sourceRef';
import type { Fraction } from '@mimir/common/util/fractionType';
import { UndoHelper, type UndoFn } from './utils/undoHelper';
import type { TimelineStateUpdaterOps } from '../public/timelineStateUpdaterOps';


export class TimelineInternalHelper implements TimelineInternalOps {

  constructor(
    private readonly timelineModel: TimelineModel,
    private readonly timelineStateUpdater: TimelineStateUpdaterOps,
  ) {}


  getMainTrackDurationInSeconds(exclusiveEnd: number | null): Fraction {
    const end = exclusiveEnd ?? this.timelineModel.videoTracks[0].length;
    let duration = { numerator: 0, denominator: 1 };
    for (let i = 0; i < end; i++) {
      const trackObject = this.timelineModel.videoTracks[0][i];
      duration = addFractions(duration, trackObject.outPointInSeconds);
      duration = subtractFractions(duration, trackObject.inPointInSeconds);
    }
    return duration;
  }


  getNumberOfAudioTracks(): number {
    return this.timelineModel.audioTracks.length;
  }


  createUniqueId(result: string[]): UndoFn {
    const id = this.timelineModel.nextUiId++;
    result.push(`id-${id}`);
    return () => this.undoCreateUniqueId();
  }


  private undoCreateUniqueId(): void {
    this.timelineModel.nextUiId--;
  }


  addMimirItemRef(ref: MimirItemRef): UndoFn {
    const existingRef = this.timelineModel.mimirItemRefs[ref.itemId];
    if (!existingRef) {
      this.timelineModel.mimirItemRefs[ref.itemId] = ref;
      invariant(this.timelineModel.mimirItemRefCounts[ref.itemId] === undefined, () => `Should not have ref count for item ${ref.itemId}`);
      this.timelineModel.mimirItemRefCounts[ref.itemId] = 1;
    } else {
      invariant(this.timelineModel.mimirItemRefCounts[ref.itemId] !== undefined, () => `Should have ref count for item ${ref.itemId}`);
      this.timelineModel.mimirItemRefCounts[ref.itemId] += 1;
      invariant(existingRef.type === ref.type, 'Mismatching types');

      if (hasDuration(existingRef) && hasDuration(ref) && lessThanFraction(existingRef.durationInSeconds, ref.durationInSeconds)) {
        existingRef.durationInSeconds = { ...ref.durationInSeconds };
      }
    }

    return () => this.undoAddMimirItemRef(ref);
  }


  /**
   * This function is meant to undo what was done by addMimirItemRef().
   * There is however an exception regarding the item duration. If a user
   * adds multiple cuts from a growing item to the timeline, it may be that
   * the known duration of the item has increased since the insertion of the
   * first cut of the item. In such a case, we update the known duration
   * of the item in the same way a call to updateItemDuration() would.
   *
   * Since the growth of an item is something that happens outside any
   * timeline, we don't want to revert to the previous known duration as it
   * would, for instance, constrain the resizability of cuts from this item
   * to an outdated maximum duration.
   */
  private undoAddMimirItemRef(ref: MimirItemRef) {
    const existingRef = this.timelineModel.mimirItemRefs[ref.itemId];
    invariant(existingRef, () => `Should have ref for item ${ref.itemId}`);

    const refCount = this.timelineModel.mimirItemRefCounts[ref.itemId];
    invariant(refCount !== undefined, () => `Should have ref count for item ${ref.itemId}`);

    if (refCount > 1) {
      this.timelineModel.mimirItemRefCounts[ref.itemId] = refCount - 1;
    } else {
      // We need to remove the item reference
      delete this.timelineModel.mimirItemRefs[ref.itemId];
      delete this.timelineModel.mimirItemRefCounts[ref.itemId];
    }
  }


  addSourceRef(ref: SourceRef): UndoFn {
    this.timelineStateUpdater.addSource(ref);
    return () => this.removeSourceRef(ref);
  }


  private removeSourceRef(ref: SourceRef): UndoFn {
    this.timelineStateUpdater.removeSource(ref);
    return () => this.addSourceRef(ref);
  }


  addVideoTrackBox(trackIndex: number, box: ModelVideoTrackBox): UndoFn {
    this.timelineStateUpdater.insertVideoTrackBox(trackIndex, this.timelineModel.videoTracks[trackIndex].length, box);
    return () => this.removeVideoTrackBox(trackIndex, box);
  }


  insertVideoTrackBox(trackIndex: number, index: number, box: ModelVideoTrackBox): UndoFn {
    this.timelineStateUpdater.insertVideoTrackBox(trackIndex, index, box);
    return () => this.removeVideoTrackBox(trackIndex, box);
  }


  private removeVideoTrackBox(trackIndex: number, box: ModelVideoTrackBox): UndoFn {
    const index = this.timelineModel.videoTracks[trackIndex].findIndex(b => b.boxId === b.boxId);
    this.timelineStateUpdater.removeVideoTrackBox(trackIndex, box);
    return () => this.insertVideoTrackBox(trackIndex, index, box);
  }


  addAudioTrackBox(trackIndex: number, box: ModelAudioTrackBox): UndoFn {
    this.timelineStateUpdater.addAudioTrackBox(trackIndex, box);
    return () => this.removeAudioTrackBox(trackIndex, box);
  }


  removeAudioTrackBox(trackIndex: number, box: ModelAudioTrackBox): UndoFn {
    this.timelineStateUpdater.removeAudioTrackBox(trackIndex, box);
    return () => this.addAudioTrackBox(trackIndex, box);
  }


  /**
   * This is a naive implementation that scans all the video/audio boxes.
   * TODO optimize using better bookkeeping
   */
  getTrackBoxLocation(box: ModelTrackBox): ModelTrackBoxLocation {
    const trackType = isModelVideoTrackBox(box) ? 'videoTrack' : 'audioTrack';
    const tracks = trackType === 'videoTrack' ? this.timelineModel.videoTracks : this.timelineModel.audioTracks;
    for (let trackIndex = 0; trackIndex < tracks.length; trackIndex++) {
      const track = tracks[trackIndex];
      for (let boxIndex = 0; boxIndex < track.length; boxIndex++) {
        if (track[boxIndex].boxId === box.boxId) {
          return {
            trackType,
            trackIndex,
            boxIndex,
          };
        }
      }
    }
    throw new Error(`Internal error: could not find box ${JSON.stringify(box)}`);
  }


  setOutPointOfTrackBoxGroup(sourceId: string, newOutPoint: Fraction, sourcesDelta: Fraction): UndoFn {
    const allBoxes = this.timelineModel.trackBoxesBySourceId[sourceId];
    const originalOutPoints: Record<string, Fraction> = {};
    let videoTrackBoxLocation: ModelTrackBoxLocation | undefined;
    allBoxes.forEach(b => {
      if (isModelVideoTrackBox(b)) {
        invariant(videoTrackBoxLocation === undefined, () => `Did not expect more than one video track box for source ${sourceId}`);
        videoTrackBoxLocation = this.getTrackBoxLocation(b);
      }
      originalOutPoints[b.boxId] = copyFrac(b.outPointInSeconds);
      this.timelineStateUpdater.setOutPoint(b, newOutPoint);
    });

    // If we have found a video track box, we need to shift all video track boxes to its right
    const sourcesToShiftOldPositions: Record<string, Fraction> = {};
    if (videoTrackBoxLocation) {
      const track = this.timelineModel.videoTracks[videoTrackBoxLocation.trackIndex];
      for (let boxIndex = videoTrackBoxLocation.boxIndex + 1; boxIndex < track.length; boxIndex++) {
        const source = track[boxIndex].sourceRef;
        sourcesToShiftOldPositions[source.srcId] = copyFrac(source.mediaStartOffset);
        this.updateSourceStart(source, addFractions(source.mediaStartOffset, sourcesDelta));
      }
    }

    return () => {
      allBoxes.forEach(b => this.timelineStateUpdater.setOutPoint(b, originalOutPoints[b.boxId]));
      Object.entries(sourcesToShiftOldPositions).forEach(([entrySourceId, oldStartValue]) => {
        const source = this.timelineModel.sourceRefs[entrySourceId];
        this.updateSourceStart(source, oldStartValue);
      });
    };
  }


  private updateSourceStart(sourceRef: SourceRef, start: Fraction) {
    this.timelineStateUpdater.setSourceStart(sourceRef, start);
    this.timelineModel.trackBoxesBySourceId[sourceRef.srcId].forEach(box => this.timelineStateUpdater.setBoxStart(box, addFractions(box.inPointInSeconds, start)))
    this.timelineStateUpdater.sortTracks();
  }


  setInPointOfTrackBoxGroup(sourceId: string, newInPoint: Fraction, sourcesDelta: Fraction): UndoFn {
    const allBoxes = this.timelineModel.trackBoxesBySourceId[sourceId];
    const originalInPoints: Record<string, Fraction> = {};
    let videoTrackBoxLocation: ModelTrackBoxLocation | undefined;
    allBoxes.forEach(b => {
      if (isModelVideoTrackBox(b)) {
        invariant(videoTrackBoxLocation === undefined, () => `Did not expect more than one video track box for source ${sourceId}`);
        videoTrackBoxLocation = this.getTrackBoxLocation(b);
      }
      originalInPoints[b.boxId] = copyFrac(b.inPointInSeconds);
      this.timelineStateUpdater.setInPoint(b, newInPoint);
    });

    // If we have found a video track box, we need to shift all video track boxes to its right
    // as well as adjusting the source position of the box itself (since updating the in point
    // requires adjusting the source start position)
    const sourcesToShiftOldPositions: Record<string, Fraction> = {};
    if (videoTrackBoxLocation) {
      const track = this.timelineModel.videoTracks[videoTrackBoxLocation.trackIndex];
      for (let boxIndex = videoTrackBoxLocation.boxIndex; boxIndex < track.length; boxIndex++) {
        const source = track[boxIndex].sourceRef;
        sourcesToShiftOldPositions[source.srcId] = copyFrac(source.mediaStartOffset);
        this.updateSourceStart(source, addFractions(source.mediaStartOffset, sourcesDelta));
      }
    }

    return () => {
      allBoxes.forEach(b => this.timelineStateUpdater.setInPoint(b, originalInPoints[b.boxId]));
      Object.entries(sourcesToShiftOldPositions).forEach(([entrySourceId, oldStartValue]) => {
        const source = this.timelineModel.sourceRefs[entrySourceId];
        this.updateSourceStart(source, oldStartValue);
      });
    };
  }


  moveMainVideoTrackBox(sourceIndex: number, targetIndex: number): UndoFn {
    return this.moveMainVideoTrackBoxes(1, sourceIndex, targetIndex);
  }


  moveMainVideoTrackBoxes(nBoxes: number, sourceIndex: number, targetIndex: number): UndoFn {
    if (sourceIndex === targetIndex) {
      return () => {};
    }

    if (targetIndex < sourceIndex) {
      // If the target index is before the source index like this:
      //
      //       target                  source
      //       index                   index
      //         v                       v
      //  ---+-------+-------+-------+-------+-------+---
      //     |   A   |   B   |   C   |   X   |   Y   |
      //  ---+-------+-------+-------+-------+-------+---
      //
      // We need to:
      // 1) remove the boxes X and Y from the source index
      // 2) insert the boxes X and Y at the target index
      // 3) decrease the start of X and Y's source by the duration of A+B+C
      // 4) increase the start of the sources of A, B and C by the duration of X+Y
      //
      // And the result is:
      //  ---+-------+-------+-------+-------+-------+---
      //     |   X   |   Y   |   A   |   B   |   C   |
      //  ---+-------+-------+-------+-------+-------+---
      const xyBoxes = this.timelineModel.videoTracks[0].slice(sourceIndex, sourceIndex + nBoxes);
      const abcBoxes = this.timelineModel.videoTracks[0].slice(targetIndex, sourceIndex);
      const xyDuration = getModelTrackBoxesDuration(xyBoxes);
      const abcDuration = getModelTrackBoxesDuration(abcBoxes);
      xyBoxes.forEach(box => this.timelineStateUpdater.removeVideoTrackBox(0, box));
      for (let i = 0; i < nBoxes; i++) {
        this.timelineStateUpdater.insertVideoTrackBox(0, targetIndex + i, xyBoxes[i]);
      }

      xyBoxes.forEach(box => this.updateSourceStart(box.sourceRef, subtractFractions(box.sourceRef.mediaStartOffset, abcDuration)));
      abcBoxes.forEach(box => this.updateSourceStart(box.sourceRef, addFractions(box.sourceRef.mediaStartOffset, xyDuration)));
    } else {
      // If the target index is after the source index like this:
      //
      //       source                  target
      //       index                   index
      //         v                       v
      //  ---+-------+-------+-------+-------+-------+---
      //     |   X   |   Y   |   A   |   B   |   C   |
      //  ---+-------+-------+-------+-------+-------+---
      //
      // We need to:
      // 1) remove the boxes X and Y from the source index
      // 2) insert the boxes X and Y at the target index
      // 3) increase the start of X and Y's source by the duration of A+B+C
      // 4) decrease the start of the sources of A, B and C by the duration of X+Y
      //
      // And the result is:
      //  ---+-------+-------+-------+-------+-------+---
      //     |   A   |   B   |   C   |   X   |   Y   |
      //  ---+-------+-------+-------+-------+-------+---
      const xyBoxes = this.timelineModel.videoTracks[0].slice(sourceIndex, sourceIndex + nBoxes);
      const abcBoxes = this.timelineModel.videoTracks[0].slice(sourceIndex + nBoxes, targetIndex + nBoxes);
      const xyDuration = getModelTrackBoxesDuration(xyBoxes);
      const abcDuration = getModelTrackBoxesDuration(abcBoxes);

      xyBoxes.forEach(box => this.timelineStateUpdater.removeVideoTrackBox(0, box));
      for (let i = 0; i < nBoxes; i++) {
        this.timelineStateUpdater.insertVideoTrackBox(0, targetIndex + i, xyBoxes[i]);
      }
      xyBoxes.forEach(box => this.updateSourceStart(box.sourceRef, addFractions(box.sourceRef.mediaStartOffset, abcDuration)));
      abcBoxes.forEach(box => this.updateSourceStart(box.sourceRef, subtractFractions(box.sourceRef.mediaStartOffset, xyDuration)));
    }

    // Undoing the move is just doing it the other way
    return () => this.moveMainVideoTrackBox(targetIndex, sourceIndex);
  }


  deleteSingleTrackBox(box: ModelTrackBox): UndoFn {
    const location = this.getTrackBoxLocation(box);
    if (location.trackType === 'videoTrack') {
      invariant(isModelVideoTrackBox(box), 'Should have video track box');
      return this.removeVideoTrackBox(location.trackIndex, box);
    } else {
      invariant(!isModelVideoTrackBox(box), 'Should not have video track box');
      return this.removeAudioTrackBox(location.trackIndex, box);
    }
  }


  deleteMainTrackBoxes(boxes: ModelVideoTrackBox[]): UndoFn {
    const undoHelper = new UndoHelper();

    const itemsToRemove = new Set<string>();
    boxes.forEach(box => itemsToRemove.add(box.boxId));

    // As we remove boxes from the main track, we need to keep
    // track of how much we need to shift the sources of the items
    // on the right
    let sourcesDelta = frac(0, 1);
    let i = 0;

    while (i < this.timelineModel.videoTracks[0].length) {
      const box = this.timelineModel.videoTracks[0][i];
      if (itemsToRemove.has(box.boxId)) {
        sourcesDelta = subtractFractions(sourcesDelta, getModelTrackBoxDuration(box));
        // We need to remove all the boxes related to this source.
        // Since deleteSingleTrackBox() is going to remove things from trackBoxesBySourceId,
        // we need to iterate over a copy of it
        const relatedBoxes = [...this.timelineModel.trackBoxesBySourceId[box.sourceRef.srcId]];
        for (const relatedBox of relatedBoxes) {
          undoHelper.add(this.deleteSingleTrackBox(relatedBox));
        }
        // And then remove the source itself
        undoHelper.add(this.removeSourceRef(box.sourceRef));
      } else {
        // We keep this box, so we just need to shift its source
        const oldStart = copyFrac(box.sourceRef.mediaStartOffset);
        this.updateSourceStart(box.sourceRef, addFractions(box.sourceRef.mediaStartOffset, sourcesDelta));
        undoHelper.add(() => this.updateSourceStart(box.sourceRef, oldStart));
        i++;
      }
    }

    return () => undoHelper.undo();
  }


  updateUndoRedoStatus(canUndo: boolean, canRedo: boolean): void {
    this.timelineStateUpdater.updateUndoRedoStatus(canUndo, canRedo);
  }


  getSourceStartOffsets(): Record<string, Fraction> {
    const mapping: Record<string, Fraction> = {};

    Object.entries(this.timelineModel.sourceRefs).forEach(([srcId, srcRef]) => {
      mapping[srcId] = copyFrac(srcRef.mediaStartOffset);
    });

    return mapping;
  }


  setSourceStartOffset(sourceId: string, offset: Fraction): UndoFn {
    const sourceRef = this.timelineModel.sourceRefs[sourceId];
    invariant(sourceRef, () => `Could not find source ref ${sourceId}`);
    const oldOffset = copyFrac(sourceRef.mediaStartOffset);
    this.updateSourceStart(sourceRef, offset);
    sourceRef.mediaStartOffset = copyFrac(offset);
    return () => this.updateSourceStart(sourceRef, oldOffset);
  }


  getMainTrackLength() {
    return this.timelineModel.videoTracks[0].length;
  }


  getMainTrackBox(index: number): ModelVideoTrackBox {
    const box = this.timelineModel.videoTracks[0][index];
    invariant(box, () => `Could not find main track box at index ${index}`);
    return box;
  }
}
